"use client";

import { HiArrowRight, HiCheck, HiMail } from "@/components/icons";
import { useNewsletterSubscription } from "@/hooks/useNewsletterSubscription";

interface NewsletterFormProps {
  className?: string;
  source?: string;
}

export default function NewsletterForm({ className = "", source = "footer" }: NewsletterFormProps) {
  const { email, emailError, status, handleSubmit, handleEmailChange, handleEmailFocus } =
    useNewsletterSubscription({
      source,
    });

  if (status === "success") {
    return (
      <div
        key="success"
        className={`relative overflow-hidden bg-foreground text-background rounded-3xl p-8 text-center ${className}`}
      >
        <div className="absolute inset-0 opacity-[0.03] bg-noise mix-blend-overlay pointer-events-none"></div>
        <div className="relative z-10">
          <div className="w-12 h-12 bg-primary/20 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <HiCheck className="text-2xl" />
          </div>
          <h2 className="text-xl font-bold mb-2">You&apos;re on the list!</h2>
          <p className="text-sm text-background/60">Keep an eye on your inbox for the goods.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      key="form"
      className={`relative overflow-hidden bg-foreground text-background rounded-3xl p-8 ${className}`}
    >
      {/* Noise overlay */}
      <div className="absolute inset-0 opacity-[0.03] bg-noise mix-blend-overlay pointer-events-none"></div>
      {/* Subtle orange glow */}
      <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-[radial-gradient(ellipse_at_top_right,_rgba(249,116,21,0.08)_0%,_transparent_70%)] pointer-events-none"></div>
      {/* Gradient top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>

      <div className="relative z-10">
        <h2 className="font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
          <HiMail className="text-primary" /> Join the inner circle
        </h2>
        <p className="text-sm text-background/60 mb-4">
          New extensions, productivity tips, and exclusive deals. Delivered monthly.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input
              id="newsletter-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onFocus={handleEmailFocus}
              required
              suppressHydrationWarning
              aria-label="Email address"
              aria-describedby={emailError ? "newsletter-email-error" : undefined}
              aria-invalid={!!emailError}
              className={`flex-1 bg-background/10 border rounded-full px-4 py-2 text-sm text-background placeholder:text-background/40 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all ${
                emailError ? "border-red-500" : "border-background/20"
              }`}
            />
            <button
              type="submit"
              disabled={status === "loading"}
              suppressHydrationWarning
              className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-bold hover:bg-primary/90 hover:scale-105 hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {status === "loading" ? (
                <span className="animate-spin">...</span>
              ) : (
                <>
                  Subscribe <HiArrowRight />
                </>
              )}
            </button>
          </div>
          {emailError && (
            <p id="newsletter-email-error" className="text-xs text-red-400" role="alert">
              {emailError}
            </p>
          )}
        </form>
        {status === "error" && (
          <p className="text-xs text-red-400 mt-2" role="alert">
            Something went wrong. Try again?
          </p>
        )}
        <p className="text-xs text-background/30 mt-3">
          We respect your inbox. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}
